<template>
  <div class="field-reference" :class="refModel.toLowerCase()">
    <template v-if="itemArray">
      <div class="list-values">
        <template v-if="Array.isArray(value)">
          <div
            v-for="(item, index) in localReferenceItems"
            :key="`${item?._id || index}`"
            class="d-flex align-items-center gap-1 mb-1"
            style="display: flex; width: 100%;"
          >
            <span
              v-if="item?.name"
              class="cursor-pointer"
              :class="item?.deleted ? 'reference-in-trash' : 'text-primary text-decoration-underline'"
              @click.stop="goToReference(item._id)"
            >
              {{ item.name }}
            </span>
            <span
              v-else
              class="cursor-pointer"
              :class="item?.deleted ? 'reference-in-trash' : 'text-primary text-decoration-underline'"
              @click.stop="goToReference(value[index])"
            >
              {{ item?.name || item?.label || "" }}
            </span>
            <i
              v-if="item?.deleted"
              class="fas fa-trash-alt text-danger ms-1"
              :title="$t('This item is deleted')"
              style="font-size: 0.9em;"
            ></i>
            <a
              v-if="item?._id || value[index]"
              :href="getReferenceUrl(item?._id || value[index])"
              target="_blank"
              rel="noopener noreferrer"
              @click.stop
              class="text-muted text-decoration-none"
              :title="$t('Open in new tab')"
              style="font-size: 0.8em;"
            >
              <i class="fas fa-external-link-alt"></i> 
            </a>
            <NotaComercialPendingBadge
              v-if="isContactReference"
              :contact-id="referenceIdString(item?._id || value[index])"
            />
          </div>
        </template>
        <template v-else>
          <span v-if="value?.name">{{ value.name }} </span>
        </template>
      </div>
    </template>
    <template v-else>
      <span v-if="value?._id" class="d-inline-flex align-items-center gap-1">
        <span
          class="cursor-pointer"
          :class="value?.deleted ? 'reference-in-trash' : 'text-primary text-decoration-underline'"
          @click.stop="goToReference(value._id)"
        >
          {{ value?.name || value }}
        </span>
        <i
          v-if="value?.deleted"
          class="fas fa-trash-alt text-danger ms-1"
          :title="$t('This item is deleted')"
          style="font-size: 0.9em;"
        ></i>
        <a
          :href="getReferenceUrl(value._id)"
          target="_blank"
          rel="noopener noreferrer"
          @click.stop
          class="text-muted text-decoration-none"
          :title="$t('Open in new tab')"
          style="font-size: 0.8em;"
        >
          <i class="fas fa-external-link-alt"></i>
        </a>
        <NotaComercialPendingBadge
          v-if="isContactReference"
          :contact-id="referenceIdString(value._id)"
        />
      </span>
      <span v-else class="d-inline-flex align-items-center gap-1">
        <span
          v-if="value?.name"
          class="cursor-pointer"
          :class="value?.deleted || localReferenceItems?.deleted ? 'reference-in-trash' : 'text-primary text-decoration-underline'"
          @click.stop="goToReference(value._id)"
        >
          {{ value.name }}
        </span>
        <span
          v-else
          class="cursor-pointer"
          :class="localReferenceItems?.deleted ? 'reference-in-trash' : 'text-primary text-decoration-underline'"
          @click.stop="goToReference(value)"
        >
          {{ localReferenceItems?.name || localReferenceItems?.label || "" }}
        </span>
        <i
          v-if="value?.deleted || localReferenceItems?.deleted"
          class="fas fa-trash-alt text-danger ms-1"
          :title="$t('This item is deleted')"
          style="font-size: 0.9em;"
        ></i>
        <a
          v-if="value?._id || value"
          :href="getReferenceUrl(value?._id || value)"
          target="_blank"
          rel="noopener noreferrer"
          @click.stop
          class="text-muted text-decoration-none"
          :title="$t('Open in new tab')"
          style="font-size: 0.8em;"
        >
          <i class="fas fa-external-link-alt"></i>
        </a>
        <NotaComercialPendingBadge
          v-if="isContactReference"
          :contact-id="referenceIdString(value?._id || value)"
        />
      </span>
    </template>
  </div>
</template>

<script>
import { wencesApi } from "@/api/wencesApi";
import { buildDynamicModelItemViewPath } from "@/utils/dynamicModelPaths";
import NotaComercialPendingBadge from "@/modules/nota_comercial_module/components/NotaComercialPendingBadge.vue";

/** Modelos de contacto: son los únicos que pueden tener notas comerciales. */
const CONTACT_REF_MODEL_RE = /^contacts?$/i;

export default {
  name: "FieldReference",
  components: { NotaComercialPendingBadge },
  data() {
    return {
      localReferenceItems: [],
      itemArray: false,
    };
  },
  props: {
    value: {
      type: [Object, Array, String],
    },
    refModel: {
      type: String,
      required: true,
    },
    isArray: {
      type: Boolean,
      default: false,
    },
    fields: {
      type: Array,
      default: () => [],
    },
    referenceItems: {
      type: [Object, Array],
      default: () => ({}),
    },
  },
  async created() {
    if (Array.isArray(this.value)) {
      this.itemArray = true;
      // Si es un array, verificar si los items ya vienen completos
      this.localReferenceItems = await Promise.all(
        this.value.map(async (item) => {
          // Si el item ya es un objeto (no un string), asumir que viene del backend y usarlo directamente
          // El backend hace populate con '_id name', así que si es un objeto con _id, ya viene poblado
          if (typeof item === 'object' && item !== null && item._id) {
            // Ya viene poblado del backend, no hacer llamada adicional
            return item;
          }
          
          // Si es un string (ID), buscar primero en referenceItems prop
          if (typeof item === 'string' && this.referenceItems) {
            if (Array.isArray(this.referenceItems)) {
              const found = this.referenceItems.find(ref => ref._id === item || ref._id?.toString() === item?.toString());
              if (found) return found;
            } else if (this.referenceItems[this.refModel]) {
              const refArray = Array.isArray(this.referenceItems[this.refModel]) ? this.referenceItems[this.refModel] : [];
              const found = refArray.find(ref => ref._id === item || ref._id?.toString() === item?.toString());
              if (found) return found;
            }
          }
          
          // Solo hacer la llamada si es un string (ID) y no se encontró en referenceItems
          if (typeof item === 'string') {
            try {
              const { data } = await wencesApi.get(
                `/api/gotrading/dynamic/${this.refModel}/${item}`
              );
              return data;
            } catch (error) {
              console.error("Error loading reference:", error);
              return null;
            }
          }
          
          // Si ya es un objeto, devolverlo
          return item;
        })
      );
    } else if (this.value) {
      this.itemArray = false;
      // Si el valor ya es un objeto con _id, asumir que viene del backend y usarlo directamente
      // El backend hace populate con '_id name', así que si es un objeto con _id, ya viene poblado
      if (typeof this.value === 'object' && this.value !== null && this.value._id) {
        // Ya viene poblado del backend, no hacer llamada adicional
        this.localReferenceItems = this.value;
        return;
      }
      
      // Si es un string (ID), buscar primero en referenceItems prop
      if (typeof this.value === 'string' && this.referenceItems) {
        if (Array.isArray(this.referenceItems)) {
          const found = this.referenceItems.find(ref => ref._id === this.value || ref._id?.toString() === this.value?.toString());
          if (found) {
            this.localReferenceItems = found;
            return;
          }
        } else if (this.referenceItems[this.refModel]) {
          const refArray = Array.isArray(this.referenceItems[this.refModel]) ? this.referenceItems[this.refModel] : [];
          const found = refArray.find(ref => ref._id === this.value || ref._id?.toString() === this.value?.toString());
          if (found) {
            this.localReferenceItems = found;
            return;
          }
        }
      }
      
      // Solo hacer la llamada si es un string (ID) y no se encontró en referenceItems
      if (typeof this.value === 'string') {
        await this.loadReferenceIfNeeded();
      } else {
        // Si ya es un objeto, usarlo directamente
        this.localReferenceItems = this.value;
      }
    }
  },
  computed: {
    /** Solo los contactos llevan el aviso de notas comerciales pendientes. */
    isContactReference() {
      return CONTACT_REF_MODEL_RE.test(String(this.refModel || "").trim());
    },
  },
  methods: {
    /** Id del referenciado, venga poblado como objeto o como string. */
    referenceIdString(value) {
      if (!value) return "";
      if (typeof value === "object") return String(value._id || "");
      return String(value);
    },
    resolveReferenceTitle(itemId) {
      const id = String(itemId || "");
      const fromList = (this.localReferenceItems || []).find(
        (entry) => entry && String(entry._id) === id
      );
      if (fromList && (fromList.name || fromList.label)) {
        return fromList.name || fromList.label;
      }
      if (
        this.value &&
        typeof this.value === "object" &&
        !Array.isArray(this.value) &&
        String(this.value._id) === id
      ) {
        return this.value.name || this.value.label || "";
      }
      return "";
    },
    goToReference(itemId) {
      if (!itemId) return;
      const url = buildDynamicModelItemViewPath(
        this.refModel,
        itemId,
        this.resolveReferenceTitle(itemId)
      );
      this.$router.push(url).catch(err => {
        // Si hay error de navegación duplicada, ignorarlo
        if (err.name !== 'NavigationDuplicated') {
          console.error('Error navigating to reference:', err);
        }
      });
    },
    getReferenceUrl(itemId) {
      if (!itemId) return '#';
      return buildDynamicModelItemViewPath(
        this.refModel,
        itemId,
        this.resolveReferenceTitle(itemId)
      );
    },
    async loadReferenceIfNeeded() {
      try {
        // Si el valor ya es un objeto con _id, asumir que viene del backend y usarlo directamente
        // El backend hace populate con '_id name', así que si es un objeto con _id, ya viene poblado
        if (typeof this.value === 'object' && this.value !== null && this.value._id) {
          this.localReferenceItems = this.value;
          return;
        }

        // Solo hacer la llamada si es un string (ID)
        if (typeof this.value === 'string') {
          const { data } = await wencesApi.get(
            `/api/gotrading/dynamic/${this.refModel}/${this.value}`
          );
          this.localReferenceItems = data;
        } else {
          // Si ya es un objeto, usarlo directamente
          this.localReferenceItems = this.value;
        }
      } catch (error) {
        console.error("Error loading reference:", error);
        this.localReferenceItems = null;
      }
    },
    getReferenceName() {
      if (this.value && this.value.name) {
        return this.value.name;
      }
      return (
        this.localReferenceItems?.label ||
        this.localReferenceItems?.name ||
        this.localReferenceItems ||
        ""
      );
    },
  },
};
</script>

<style scoped>
.field-reference .badge {
  font-size: 0.8em;
  padding: 4px 8px;
}

.field-reference a {
  color: inherit;
}

.field-reference a:hover {
  text-decoration: underline !important;
}

.list-values {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.list-values > div {
  display: flex;
  align-items: center;
  width: 100%;
}

.reference-in-trash {
  color: #b02a37 !important;
  text-decoration: line-through;
  font-weight: 600;
  cursor: pointer;
}
.reference-in-trash:hover {
  color: #8b222a !important;
}
</style>
